import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { templatesAPI } from '../services/api';
import { 
  FileText, 
  Truck, 
  Users, 
  BarChart3,
  Search,
  Filter,
  Loader2,
  ChevronRight,
  Database,
  Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const categoryIcons = {
  fleet: Truck,
  driver: Users,
  management: BarChart3,
};

const categoryColors = {
  fleet: 'bg-blue-100 text-blue-700 border-blue-200',
  driver: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  management: 'bg-purple-100 text-purple-700 border-purple-200',
};

const TemplateCard = ({ template, onNewSubmission, onViewData }) => {
  const Icon = categoryIcons[template.category] || FileText;
  
  return (
    <Card 
      className="hover:shadow-md transition-all"
      data-testid={`template-card-${template.code}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${categoryColors[template.category]}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-sm font-bold text-slate-500">{template.code}</span>
              <Badge variant="outline" className="text-xs uppercase">
                {template.category}
              </Badge>
            </div>
            <h3 className="font-semibold text-slate-900 truncate">{template.name}</h3>
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{template.description}</p>
            <div className="flex gap-2 mt-3">
              <Button 
                size="sm" 
                onClick={onNewSubmission}
                className="gap-1"
                data-testid={`new-submission-${template.code}`}
              >
                <Plus className="w-3 h-3" />
                New
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={onViewData}
                className="gap-1"
                data-testid={`view-data-${template.code}`}
              >
                <Database className="w-3 h-3" />
                View Data
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const TemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await templatesAPI.list();
        setTemplates(response.data);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const filteredTemplates = templates.filter(template => {
    // Hide F011 (Weight Records) as per user request
    // Hide F004 (Daily Vehicle Inspection) - has its own dedicated page
    if (template.code === 'F011' || template.code === 'F004') return false;
    
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'all' || template.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleTemplateClick = (template) => {
    // Special handling for F001 (Fleet Inventory) - redirect to Vehicles page
    if (template.code === 'F001') {
      navigate('/vehicles?add=true');
      return;
    }
    // Special handling for D001 (Driver Register) - redirect to Drivers page
    if (template.code === 'D001') {
      navigate('/drivers?add=true');
      return;
    }
    // Special handling for F005 (Service Schedule) - redirect to Service Schedule page
    if (template.code === 'F005') {
      navigate('/service-schedule');
      return;
    }
    // Special handling for F006 (Tyre Survey) - redirect to Tyre Survey page
    if (template.code === 'F006') {
      navigate('/tyre-survey');
      return;
    }
    // Special handling for F007 (Breakdown Report) - redirect to Breakdowns page
    if (template.code === 'F007') {
      navigate('/breakdowns');
      return;
    }
    // Special handling for F008 (Accident Report) - redirect to Accidents page
    if (template.code === 'F008') {
      navigate('/accidents');
      return;
    }
    // Special handling for F009 (Fine Schedule) - redirect to Fine Schedule page
    if (template.code === 'F009') {
      navigate('/fines');
      return;
    }
    // Special handling for F010 (Fuel Schedule) - redirect to Fuel Schedule page
    if (template.code === 'F010') {
      navigate('/fuel-schedule');
      return;
    }
    navigate(`/submissions/new/${template.code}`);
  };

  const handleViewData = (template) => {
    // Special handling for F005 (Service Schedule) - redirect to Service Schedule page
    if (template.code === 'F005') {
      navigate('/service-schedule');
      return;
    }
    // Special handling for F006 (Tyre Survey) - redirect to Tyre Survey page
    if (template.code === 'F006') {
      navigate('/tyre-survey?tab=surveys');
      return;
    }
    // Special handling for F007 (Breakdown Report) - redirect to Breakdowns page
    if (template.code === 'F007') {
      navigate('/breakdowns');
      return;
    }
    // Special handling for F008 (Accident Report) - redirect to Accidents page
    if (template.code === 'F008') {
      navigate('/accidents');
      return;
    }
    // Special handling for F009 (Fine Schedule) - redirect to Fine Schedule page
    if (template.code === 'F009') {
      navigate('/fines');
      return;
    }
    // Special handling for F010 (Fuel Schedule) - redirect to Fuel Schedule page
    if (template.code === 'F010') {
      navigate('/fuel-schedule');
      return;
    }
    navigate(`/form-data/${template.code}`);
  };

  // Filter out F011 and F004 from category counts
  const visibleTemplates = templates.filter(t => t.code !== 'F011' && t.code !== 'F004');
  
  const categoryCounts = {
    all: visibleTemplates.length,
    fleet: visibleTemplates.filter(t => t.category === 'fleet').length,
    driver: visibleTemplates.filter(t => t.category === 'driver').length,
    management: visibleTemplates.filter(t => t.category === 'management').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            FORM TEMPLATES
          </h1>
          <p className="text-slate-500 mt-1">
            Select a template to create a new submission
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search templates by name or code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12"
          data-testid="templates-search"
        />
      </div>

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="w-full justify-start bg-white border border-slate-200 p-1 h-auto flex-wrap">
          <TabsTrigger 
            value="all"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2"
            data-testid="tab-all"
          >
            All ({categoryCounts.all})
          </TabsTrigger>
          <TabsTrigger 
            value="fleet"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2"
            data-testid="tab-fleet"
          >
            <Truck className="w-4 h-4 mr-2" />
            Fleet ({categoryCounts.fleet})
          </TabsTrigger>
          <TabsTrigger 
            value="driver"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2"
            data-testid="tab-driver"
          >
            <Users className="w-4 h-4 mr-2" />
            Driver ({categoryCounts.driver})
          </TabsTrigger>
          <TabsTrigger 
            value="management"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2"
            data-testid="tab-management"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Management ({categoryCounts.management})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No templates found</h3>
          <p className="text-slate-500 mt-1">Try adjusting your search or filter criteria</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredTemplates.map((template) => (
            <TemplateCard 
              key={template.id} 
              template={template}
              onNewSubmission={() => handleTemplateClick(template)}
              onViewData={() => handleViewData(template)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplatesPage;
